// src/utils/scoring.js
// --- Qualitative labels ---
const buckets = (v, labels) => {
  if (v == null || isNaN(v)) return '';
  if (v <= 2) return labels[0];
  if (v <= 4) return labels[1];
  if (v <= 6) return labels[2];
  if (v <= 8) return labels[3];
  return labels[4];
};

// Insight Score = average insight_sentiment_score × occurrences
export function computeInsightScore({ averageSentiment, occurrences }) {
  const A = Math.max(0, Number(averageSentiment) || 0);
  const N = Math.max(0, Number(occurrences) || 0);
  return A * N;
}

export function describe(metricKey, v) {
  const L = {
    positive_importance: ["Irrelevant", "Minor nice-to-have", "Helpful", "Important", "Decisive"],
    drawback_importance: ["Negligible", "Minor annoyance", "Moderate pain", "Serious pain", "Deal-breaker"],
    satisfaction_level: ["Severe pain", "High friction", "Workable", "Smooth", "Excellent"],
    reason_not_chosen_importance: ["Irrelevant", "Minor factor", "Contributing factor", "Major factor", "Decisive veto"],
    importance: ["Very low", "Low", "Moderate", "High", "Very high"],
    satisfaction: ["Severe pain", "High friction", "Workable", "Smooth", "Excellent"],
    insight_sentiment_score: ["Very weak", "Mild", "Moderate", "Strong", "Very strong"],
    stickiness_level_of_previous_solution: ["No hesitation", "Low hesitation", "Some hesitation", "High hesitation", "Cannot switch"]
  };
  const labels = L[metricKey];
  return labels ? buckets(Number(v), labels) : '';
}

export function labelWithQual(metricKey, v, numFmt = (x)=>x) {
  const q = describe(metricKey, v);
  return `${numFmt(v)}${q ? ` · ${q}` : ''}`;
}

// --- Win/Gap scoring formulas (front-end computation) ---
// Win Score (Harmonic lift, tempered): ((2 * I * S) / (I + S)) * sqrt(N)
export function computeWinScore({ importance, satisfaction, interviews }) {
  const I = Math.max(0, Number(importance) || 0);
  const S = Math.max(0, Number(satisfaction) || 0);
  const N = Math.max(0, Number(interviews) || 0);
  if ((I + S) === 0) return 0;
  return ((2 * I * S) / (I + S)) * Math.sqrt(N);
}

// Gap Score (Importance-weighted gap): I * max(I - S, 0)
export function computeGapScore({ importance, satisfaction }) {
  const I = Math.max(0, Number(importance) || 0);
  const S = Math.max(0, Number(satisfaction) || 0);
  return I * Math.max(I - S, 0);
}

// Highlight thresholds for "gap" tiles
export const LARGE_GAP_THRESHOLD = 12;
export const HUGE_GAP_THRESHOLD = 20;

// --- Percent helper ---
export function percentOf(part, whole) {
  const p = (Number(part) || 0);
  const w = (Number(whole) || 0);
  if (w <= 0) return 0;
  return Math.round((p / w) * 100);
}

/* ------------------------------------------------------------------ */
/* ---------------- Impressions scoring (shared) --------------------- */
/* ------------------------------------------------------------------ */
// --- Impression Scores (shared by tiles & heatmaps) ---
// Positivity Score = average positive_importance × mentions
export function computePositivityScore({ averageImportance, mentions }) {
  const I = Math.max(0, Number(averageImportance) || 0);
  const N = Math.max(0, Number(mentions) || 0);
  return I * N;
}

// Negativity Score = average drawback_importance × mentions
export function computeNegativityScore({ averageImportance, mentions }) {
  const I = Math.max(0, Number(averageImportance) || 0);
  const N = Math.max(0, Number(mentions) || 0);
  return I * N;
}

